// List-shaped add/remove planners: select-item, from-item, group-by item,
// order-by item. All four share the same comma-separated shape; the
// per-clause helpers compose around `planListInsert` / `planListRemove`
// plus clause-specific empty-list / last-item handling.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — add/remove.

import type { PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { outsideLineComments } from './wholePredicate';
import {
  CLAUSE_BOUNDARIES_AFTER_FROM,
  CLAUSE_BOUNDARIES_AFTER_GROUP_BY,
  CLAUSE_BOUNDARIES_AFTER_ORDER_BY,
  CLAUSE_BOUNDARIES_AFTER_WHERE,
  deleteEdit,
  detectListSeparator,
  findByPairedClause,
  findClauseInsertionPoint,
  findKeyword,
  insertEdit,
  pickClauseInsertOffset,
  replaceEdit,
  scanStatementKeywords,
  trimLeadingWs,
  trimTrailingWs,
  type ListItem,
} from './applyStructuralEdit.shared';

// Generic comma-separated list insert. Returns one PlannedByteEdit.
export function planListInsert(
  ctx: EditCtx,
  items: readonly ListItem[],
  text: string,
  afterIndex: number | undefined,
  rationale: string,
): PlannedByteEdit {
  const sep = detectListSeparator(ctx.source, items);
  const last = items.length - 1;

  // After an item that ends in a `--` / `#` line comment (`StatusCd = 1 --ar
  // applied`) the new item starts on the next line — the comment would
  // otherwise swallow it.
  const after = (at: number): PlannedByteEdit => {
    const safe = outsideLineComments(ctx, at, at, sep + text);
    // The separator's own line break would leave a lone `,` line after the
    // comment: `\n<indent>, item` instead.
    const indent = /\n([ \t]*)$/.exec(sep)?.[1];
    const text2 = safe.startsWith('\n') && indent !== undefined ? `\n${indent}, ${text}` : safe;
    return insertEdit(ctx, at, text2, rationale);
  };
  if (afterIndex === undefined || afterIndex >= last) {
    // Append after last item.
    return after(items[last].range.endOffset);
  }
  if (afterIndex < 0) {
    // Before first item.
    return insertEdit(ctx, items[0].range.startOffset, text + sep, rationale);
  }
  // Middle insert: after items[afterIndex], before items[afterIndex+1].
  return after(items[afterIndex].range.endOffset);
}

// Generic comma-separated list remove. Returns one PlannedByteEdit covering
// the item plus its adjacent separator. Caller must handle "removing the
// only item" (which often requires also removing the clause keyword).
export function planListRemove(
  ctx: EditCtx,
  items: readonly ListItem[],
  index: number,
  rationale: string,
): PlannedByteEdit | null {
  if (index < 0 || index >= items.length) return null;
  if (items.length === 1) {
    return deleteEdit(ctx, items[0].range.startOffset, items[0].range.endOffset, rationale);
  }
  let start: number; let end: number;
  if (index === 0) {
    start = items[0].range.startOffset;
    end = items[1].range.startOffset;
  } else {
    start = items[index - 1].range.endOffset;
    end = items[index].range.endOffset;
  }
  return deleteEdit(ctx, start, end, rationale);
}

// SELECT-ITEM ----------------------------------------------------------------

export function planSelectItemEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-select-item' | 'remove-select-item' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.selectItems ?? [];
  if (edit.op === 'add-select-item') {
    if (items.length === 0) {
      // No existing items — extremely unusual SELECT shape (e.g. partial input).
      // Fall back to inserting at start of the statement; caller is responsible
      // for ensuring the SELECT is otherwise well-formed.
      return [insertEdit(ctx, ctx.statementInfo.range.startOffset, edit.text, 'add-select-item:empty')];
    }
    return [planListInsert(ctx, items, edit.text, edit.afterIndex, 'add-select-item')];
  }
  const planned = planListRemove(ctx, items, edit.index, 'remove-select-item');
  return planned ? [planned] : [];
}

// Reorder of SELECT items is a first-class byte-splice op (`move-select-item`),
// planned by `planListMove` in applyStructuralEdit.move.ts — not a whole-list
// re-emit here. See that module for the two-edit gap-aware splice.

// FROM-ITEM ------------------------------------------------------------------

export function planFromItemEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-from-item' | 'remove-from-item' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.fromItems ?? [];
  if (edit.op === 'add-from-item') {
    if (items.length === 0) {
      // No FROM clause yet — synthesize one at the position after the last
      // select-list item (or before the next clause boundary).
      return [insertFromClause(ctx, edit.text)];
    }
    return [planListInsert(ctx, items, edit.text, edit.afterIndex, 'add-from-item')];
  }
  const planned = planListRemove(ctx, items, edit.index, 'remove-from-item');
  if (!planned) return [];
  // When removing the only FROM item, also drop the leading FROM keyword.
  if (items.length === 1) {
    const fromKw = findKeyword(scanStatementKeywords(ctx), 'FROM');
    if (fromKw) {
      return [
        deleteEdit(ctx, fromKw.start, items[0].range.endOffset, 'remove-from-item:with-keyword'),
      ];
    }
  }
  return [planned];
}

export function planChangeFromItemTableEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'change-from-item-table' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.fromItems ?? [];
  if (edit.index < 0 || edit.index >= items.length) {
    throw new Error(
      `change-from-item-table: index ${edit.index} out of range (FROM list has ${items.length} item(s))`,
    );
  }
  const item = items[edit.index];
  return [replaceEdit(ctx, item.range.startOffset, item.range.endOffset, edit.text, 'change-from-item-table')];
}

function insertFromClause(ctx: EditCtx, text: string): PlannedByteEdit {
  const kws = scanStatementKeywords(ctx);
  const selectItems = ctx.structure.selectItems ?? [];
  const after = selectItems.length
    ? selectItems[selectItems.length - 1].range.endOffset
    : ctx.statementInfo.range.startOffset;
  const insertAt = findClauseInsertionPoint(
    kws, after, CLAUSE_BOUNDARIES_AFTER_FROM, ctx.statementInfo.range.endOffset,
  );
  const trimmedAt = pickClauseInsertOffset(
    ctx.source, after, insertAt, ctx.statementInfo.range.startOffset,
  );
  return insertEdit(ctx, trimmedAt, ` FROM ${text}`, 'add-from-item:empty');
}

// GROUP-BY -------------------------------------------------------------------

export function planGroupByEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-group-by' | 'remove-group-by' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.groupBy ?? [];
  if (edit.op === 'add-group-by') {
    if (items.length === 0) {
      return [insertGroupByClause(ctx, edit.columnText)];
    }
    return [planListInsert(ctx, items, edit.columnText, undefined, 'add-group-by')];
  }
  const planned = planListRemove(ctx, items, edit.index, 'remove-group-by');
  if (!planned) return [];
  if (items.length === 1) {
    const kws = scanStatementKeywords(ctx);
    const grp = findByPairedClause(ctx.source, kws, 'GROUP');
    if (grp) {
      return [
        deleteEdit(ctx, grp.keyword.start, items[0].range.endOffset, 'remove-group-by:with-keyword'),
      ];
    }
  }
  return [planned];
}

function insertGroupByClause(ctx: EditCtx, text: string): PlannedByteEdit {
  const kws = scanStatementKeywords(ctx);
  const after = clauseTailAfterFromAndWhere(ctx);
  const insertAt = findClauseInsertionPoint(
    kws, after, CLAUSE_BOUNDARIES_AFTER_GROUP_BY, ctx.statementInfo.range.endOffset,
  );
  const trimmedAt = pickClauseInsertOffset(
    ctx.source, after, insertAt, ctx.statementInfo.range.startOffset,
  );
  return insertEdit(ctx, trimmedAt, ` GROUP BY ${text}`, 'add-group-by:empty');
}

// ORDER-BY -------------------------------------------------------------------

export function planOrderByEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-order-by' | 'remove-order-by' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.orderBy ?? [];
  if (edit.op === 'add-order-by') {
    if (items.length === 0) {
      return [insertOrderByClause(ctx, edit.clauseText)];
    }
    return [planListInsert(ctx, items, edit.clauseText, edit.afterIndex, 'add-order-by')];
  }
  const planned = planListRemove(ctx, items, edit.index, 'remove-order-by');
  if (!planned) return [];
  if (items.length === 1) {
    const kws = scanStatementKeywords(ctx);
    const ord = findByPairedClause(ctx.source, kws, 'ORDER');
    if (ord) {
      return [
        deleteEdit(ctx, ord.keyword.start, items[0].range.endOffset, 'remove-order-by:with-keyword'),
      ];
    }
  }
  return [planned];
}

function insertOrderByClause(ctx: EditCtx, text: string): PlannedByteEdit {
  const kws = scanStatementKeywords(ctx);
  const after = clauseTailAfterFromAndWhere(ctx);
  const insertAt = findClauseInsertionPoint(
    kws, after, CLAUSE_BOUNDARIES_AFTER_ORDER_BY, ctx.statementInfo.range.endOffset,
  );
  const trimmedAt = pickClauseInsertOffset(
    ctx.source, after, insertAt, ctx.statementInfo.range.startOffset,
  );
  return insertEdit(ctx, trimmedAt, ` ORDER BY ${text}`, 'add-order-by:empty');
}

// Helper: find the offset just past the last existing clause among
// FROM/JOIN/WHERE/GROUP/HAVING — used as the "earliest tail offset" before
// inserting a new GROUP BY / ORDER BY clause.
function clauseTailAfterFromAndWhere(ctx: EditCtx): number {
  const { structure } = ctx;
  const candidates: number[] = [];
  if (structure.fromItems?.length) {
    candidates.push(structure.fromItems[structure.fromItems.length - 1].range.endOffset);
  }
  if (structure.joins?.length) {
    candidates.push(structure.joins[structure.joins.length - 1].range.endOffset);
  }
  if (structure.where?.length) {
    candidates.push(structure.where[structure.where.length - 1].range.endOffset);
  }
  if (structure.groupBy?.length) {
    candidates.push(structure.groupBy[structure.groupBy.length - 1].range.endOffset);
  }
  if (structure.having?.length) {
    candidates.push(structure.having[structure.having.length - 1].range.endOffset);
  }
  if (candidates.length === 0) {
    if (structure.selectItems?.length) {
      return structure.selectItems[structure.selectItems.length - 1].range.endOffset;
    }
    return ctx.statementInfo.range.startOffset;
  }
  return Math.max(...candidates);
}

// Re-exported for completeness (where-edit module uses these too).
export {
  trimLeadingWs,
  trimTrailingWs,
  CLAUSE_BOUNDARIES_AFTER_WHERE,
};
